import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { PlayerDetailData, PlayerTrends } from '@/hooks/use-coach-data';

interface OverviewTabProps {
  data: PlayerDetailData;
  trends: PlayerTrends | null;
}

function TrendIcon({ trend }: { trend: 'improving' | 'declining' | 'stable' }) {
  if (trend === 'improving') return <TrendingUp className="w-4 h-4 text-green-500" />;
  if (trend === 'declining') return <TrendingDown className="w-4 h-4 text-red-500" />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
}

export function OverviewTab({ data, trends }: OverviewTabProps) {
  return (
    <div className="space-y-4">
      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center p-3 bg-muted/40 rounded-lg">
          <p className={cn(
            'text-2xl font-bold',
            data.player.attendancePercent >= 80 ? 'text-green-600' :
            data.player.attendancePercent >= 50 ? 'text-yellow-600' : 'text-red-600'
          )}>
            {data.player.attendancePercent}%
          </p>
          <p className="text-xs text-muted-foreground">Attendance</p>
        </div>
        <div className="text-center p-3 bg-muted/40 rounded-lg">
          <p className="text-2xl font-bold text-foreground">
            {data.player.totalEvaluations}
          </p>
          <p className="text-xs text-muted-foreground">Evaluations</p>
        </div>
        <div className="text-center p-3 bg-muted/40 rounded-lg">
          <p className="text-2xl font-bold text-foreground">
            {data.stats.evalAverages
              ? (() => {
                  const vals = Object.values(data.stats.evalAverages).filter((v): v is number => v != null);
                  return vals.length > 0 ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : '-';
                })()
              : '-'}
          </p>
          <p className="text-xs text-muted-foreground">Avg Score</p>
        </div>
      </div>

      {/* Evaluation Averages with Trends */}
      {data.stats.evalAverages && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Evaluation Averages</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(data.stats.evalAverages).filter(([, v]) => v != null).map(([key, value]) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground capitalize flex-1 min-w-0">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-20 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${((value ?? 0) / 4) * 100}%` }}
                    />
                  </div>
                  <span className="font-medium w-10 text-sm">{value ?? 0}/4</span>
                  {trends?.trends && (
                    <TrendIcon trend={trends.trends[key as keyof typeof trends.trends]} />
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Recent Evaluations Preview */}
      {data.evaluations.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Recent Evaluations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.evaluations.slice(0, 3).map(eval_ => (
                <div key={eval_.id} className="p-2 bg-muted/40 rounded-lg">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-2 text-xs flex-wrap">
                      <span>Att: <strong>{eval_.attitude}</strong></span>
                      <span>Eff: <strong>{eval_.effort}</strong></span>
                      <span>Eng: <strong>{eval_.engagement}</strong></span>
                      {eval_.physical && <span>Phy: <strong>{eval_.physical}</strong></span>}
                      {eval_.technical && <span>Tec: <strong>{eval_.technical}</strong></span>}
                      <span>Tac: <strong>{eval_.tactical ?? eval_.competitionLevel}</strong></span>
                    </div>
                    <span className="text-xs text-muted-foreground ml-2 whitespace-nowrap">
                      {new Date(eval_.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                  {eval_.notes && (
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{eval_.notes}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
